<svelte:options runes={true} />

<script lang="ts">
  import type { CommandManager, ToolManager } from '@commands';
  import { Orientation } from '@geom';
  import Tool from './Tool.svelte';
  import type { ToolboxLayoutConfig } from './ToolboxLayoutConfig';

  type LayoutConfig = ToolboxLayoutConfig;

  let {
    toolManager,
    commandManager,
    layoutOrientation = $bindable(Orientation.HORIZONTAL)
  }: {
    toolManager: ToolManager;
    commandManager: CommandManager;
    layoutOrientation?: LayoutConfig;
  } = $props();

  // Calculate layout properties based on the configuration
  let layoutInfo = $derived.by(() => {
    const toolCount = toolManager.tools.length;

    if (typeof layoutOrientation === 'string') {
      // Handle legacy Orientation enum values
      switch (layoutOrientation) {
        case Orientation.HORIZONTAL:
          return {
            type: 'flex' as const,
            direction: 'row' as const,
            gridCols: toolCount,
            gridRows: 1
          };
        case Orientation.VERTICAL:
          return {
            type: 'flex' as const,
            direction: 'column' as const,
            gridCols: 1,
            gridRows: toolCount
          };
      }
    }

    if (typeof layoutOrientation === 'object') {
      if ('width' in layoutOrientation) {
        // Grid layout with specified width (columns)
        const cols = layoutOrientation.width;
        const rows = Math.ceil(toolCount / cols);
        return {
          type: 'grid' as const,
          direction: 'row' as const,
          gridCols: cols,
          gridRows: rows
        };
      }

      if ('height' in layoutOrientation) {
        // Grid layout with specified height (rows)
        const rows = layoutOrientation.height;
        const cols = Math.ceil(toolCount / rows);
        return {
          type: 'grid' as const,
          direction: 'column' as const,
          gridCols: cols,
          gridRows: rows
        };
      }
    }

    // Fallback to horizontal
    return {
      type: 'flex' as const,
      direction: 'row' as const,
      gridCols: toolCount,
      gridRows: 1
    };
  });

  // Generate the CSS class based on layout type
  function divClass() {
    return layoutInfo.type === 'flex'
      ? layoutInfo.direction === 'row'
        ? 'h-stack'
        : 'v-stack'
      : 'grid-layout';
  }

  // Generate CSS custom properties for grid layout
  function gridStyle() {
    if (layoutInfo.type === 'grid') {
      return `--grid-cols: ${layoutInfo.gridCols}; --grid-rows: ${layoutInfo.gridRows};`;
    }
    return '';
  }
</script>

<style>
  .h-stack {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
  }
  .v-stack {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .grid-layout {
    display: grid;
    grid-template-columns: repeat(var(--grid-cols), 1fr);
    grid-template-rows: repeat(var(--grid-rows), 1fr);
    gap: 0.5rem;
  }
</style>

<div style={gridStyle()} class={divClass()}>
  {#each toolManager.tools as command}
    <Tool commandId={command} {commandManager} {toolManager} />
  {/each}
</div>
